import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { Shift, ShiftGroup, HolidayCalendar, ShiftAssignment, ShiftAssignmentMember, RotationGroup, AttendanceCorrectionRequest } from '../../core/models/attendance.model';
import { environment } from '../../../environments/environment';
import { BYPASS_MOCK } from '../constants/http-context-tokens';

// ---------------------------------------------------------------------------
// Seed data mirrors the acceptance scenarios from the spec
// ---------------------------------------------------------------------------

let shiftIdSeq = Date.now();
let groupIdSeq = Date.now() + 1;

const nextShiftId = () => `s_${shiftIdSeq++}`;
const nextGroupId = () => `g_${groupIdSeq++}`;

const WD_OT = (name: string, threshold: number, multiplier1: string, multiplier2: string) => ([{
  name,
  type: 'AUTO_CALCULATE' as const,
  standardHoursThreshold: threshold,
  thresholdPeriod: 'DAILY' as const,
  otLimitHours: 4,
  otLimitPeriod: 'DAILY' as const,
  minimumOTMinutes: 30,
  roundingMinutes: 30,
  deductShortfall: true,
  shortfallDeductionCap: null,
  timeTiers: [
    { periodType: 'FIRST_N' as const, hours: 4, fromHours: 0, toHours: 0, label: 'First 4H', multiplier: multiplier1 },
    { periodType: 'BEYOND_N' as const, hours: 4, fromHours: 0, toHours: 0, label: 'Beyond 4H', multiplier: multiplier2 }
  ]
}]);

const OFF_OT = (multiplier: string) => ([{
  name: 'Off Day OT',
  type: 'AUTO_CALCULATE' as const,
  standardHoursThreshold: 0,
  thresholdPeriod: 'DAILY' as const,
  otLimitHours: 8,
  otLimitPeriod: 'DAILY' as const,
  minimumOTMinutes: 60,
  roundingMinutes: 30,
  deductShortfall: false,
  shortfallDeductionCap: null,
  timeTiers: [{ periodType: 'FLAT' as const, hours: 0, fromHours: 0, toHours: 0, label: 'All OT Hours', multiplier }]
}]);

const HOLIDAY_OT = () => ([{
  name: 'Holiday OT',
  type: 'PRE_APPROVAL' as const,
  standardHoursThreshold: 0,
  thresholdPeriod: 'DAILY' as const,
  otLimitHours: 8,
  otLimitPeriod: 'DAILY' as const,
  minimumOTMinutes: 60,
  roundingMinutes: 30,
  deductShortfall: false,
  shortfallDeductionCap: null,
  timeTiers: [{ periodType: 'FLAT' as const, hours: 0, fromHours: 0, toHours: 0, label: 'All OT Hours', multiplier: '3.0' }]
}]);

const LATE_POLICY = () => ([{
  ruleType: 'OCCURRENCE' as const,
  condition: 'LATE_ARRIVAL' as const,
  occurrenceCount: 3,
  timeframe: '1_MONTH' as const,
  action: 'DEDUCT_LEAVE' as const,
  actionValue: 'Casual Leave',
  actionAmount: 1
}]);

const MON_FRI = [
  { day: 'MON' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'TUE' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'WED' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'THU' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'FRI' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'SAT' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' },
  { day: 'SUN' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' }
];

const MON_SAT = [
  { day: 'MON' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'TUE' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'WED' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'THU' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'FRI' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'SAT' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
  { day: 'SUN' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' }
];

const MOCK_SHIFTS: Shift[] = [
  {
    id: '1',
    name: 'IT Standard',
    type: 'FIXED',
    isMultiDay: false,
    crossesMidnight: false,
    weekSchedule: MON_FRI,
    dayTypeConfigs: [
      {
        typeKey: 'WORKING_DAY', label: 'Working Day',
        workingDayConfig: {
          startTime: '08:30', startGraceMinutes: 10,
          endTime: '17:30',   endGraceMinutes: 10,
          punchInFrom: '07:30', punchInTo: '09:30',
          punchOutFrom: '16:30', punchOutTo: '18:30', spanDays: 2
        },
        otRules: WD_OT('Standard OT', 9, '1.5', '2.0'),
        breaks: [{ name: 'Lunch', isPaid: false, durationMinutes: 60, startTime: '12:30', endTime: '13:30' }],
        attendancePolicies: LATE_POLICY()
      },
      { typeKey: 'OFF_DAY',  label: 'Off Day',  otRules: OFF_OT('2.0'),    breaks: [], attendancePolicies: [] },
      { typeKey: 'HOLIDAY',  label: 'Holiday',  otRules: HOLIDAY_OT(),     breaks: [], attendancePolicies: [] }
    ],
    blocks: [{ id: '1', sequence: 1, startTime: '08:30', endTime: '17:30', startGraceMinutes: 10, endGraceMinutes: 10 }],
    breaks: [{ id: '1', name: 'Lunch', isPaid: false, durationMinutes: 60, startTime: '12:30', endTime: '13:30' }],
    otRules: [],
    createdAt: new Date().toISOString(),
    legalEntities: [{ code: 'C003', name: 'Online Accounting', type: 'company', country: 'United Kingdom' }]
  },

  {
    id: '2',
    name: 'Factory Morning',
    type: 'FIXED',
    isMultiDay: false,
    crossesMidnight: false,
    weekSchedule: MON_SAT,
    dayTypeConfigs: [
      {
        typeKey: 'WORKING_DAY', label: 'Working Day',
        workingDayConfig: {
          startTime: '07:00', startGraceMinutes: 5,
          endTime: '15:00',   endGraceMinutes: 5,
          punchInFrom: '06:00', punchInTo: '08:00',
          punchOutFrom: '14:00', punchOutTo: '16:00', spanDays: 2
        },
        otRules: WD_OT('Standard OT', 8, '1.5', '2.0'),
        breaks: [
          { name: 'Morning Tea', isPaid: true,  durationMinutes: 15, startTime: '09:00', endTime: '09:15' },
          { name: 'Lunch',       isPaid: false, durationMinutes: 30, startTime: '11:30', endTime: '12:00' },
          { name: 'Afternoon Tea', isPaid: true, durationMinutes: 15, startTime: '13:00', endTime: '13:15' }
        ],
        attendancePolicies: LATE_POLICY()
      },
      { typeKey: 'OFF_DAY',  label: 'Off Day',  otRules: OFF_OT('2.0'),  breaks: [], attendancePolicies: [] },
      { typeKey: 'HOLIDAY',  label: 'Holiday',  otRules: HOLIDAY_OT(),   breaks: [], attendancePolicies: [] }
    ],
    blocks: [{ id: '2', sequence: 1, startTime: '07:00', endTime: '15:00', startGraceMinutes: 5, endGraceMinutes: 5 }],
    breaks: [
      { id: '2a', name: 'Morning Tea',   isPaid: true,  durationMinutes: 15, startTime: '09:00', endTime: '09:15' },
      { id: '2b', name: 'Lunch',         isPaid: false, durationMinutes: 30, startTime: '11:30', endTime: '12:00' },
      { id: '2c', name: 'Afternoon Tea', isPaid: true,  durationMinutes: 15, startTime: '13:00', endTime: '13:15' }
    ],
    otRules: [],
    createdAt: new Date().toISOString(),
    legalEntities: [{ code: 'C004', name: 'Online Solutions', type: 'company', country: 'Sri Lanka' }]
  },

  {
    id: '3',
    name: 'Factory Night',
    type: 'FIXED',
    isMultiDay: true,
    crossesMidnight: true,
    weekSchedule: MON_FRI,
    dayTypeConfigs: [
      {
        typeKey: 'WORKING_DAY', label: 'Working Day',
        workingDayConfig: {
          startTime: '23:00', startGraceMinutes: 10,
          endTime: '07:00',   endGraceMinutes: 10,
          punchInFrom: '22:00', punchInTo: '00:00',
          punchOutFrom: '06:00', punchOutTo: '08:00', spanDays: 2
        },
        otRules: WD_OT('Night OT', 8, '1.5', '2.0'),
        breaks: [{ name: 'Meal Break', isPaid: false, durationMinutes: 30 }],
        attendancePolicies: []
      },
      { typeKey: 'OFF_DAY',  label: 'Off Day',  otRules: OFF_OT('2.0'),  breaks: [], attendancePolicies: [] },
      { typeKey: 'HOLIDAY',  label: 'Holiday',  otRules: HOLIDAY_OT(),   breaks: [], attendancePolicies: [] }
    ],
    blocks: [{ id: '3', sequence: 1, startTime: '23:00', endTime: '07:00', startGraceMinutes: 10, endGraceMinutes: 10 }],
    breaks: [{ id: '3a', name: 'Meal Break', isPaid: false, durationMinutes: 30 }],
    otRules: [],
    createdAt: new Date().toISOString(),
    legalEntities: [{ code: 'C004', name: 'Online Solutions', type: 'company', country: 'Sri Lanka' }]
  },

  {
    id: '4',
    name: 'Saturday Half-Day',
    type: 'FIXED',
    isMultiDay: false,
    crossesMidnight: false,
    weekSchedule: [
      { day: 'MON' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' },
      { day: 'TUE' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' },
      { day: 'WED' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' },
      { day: 'THU' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' },
      { day: 'FRI' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' },
      { day: 'SAT' as const, status: 'WORKING' as const, dayTypeKey: 'WORKING_DAY' },
      { day: 'SUN' as const, status: 'OFF' as const, dayTypeKey: 'OFF_DAY' }
    ],
    dayTypeConfigs: [
      {
        typeKey: 'WORKING_DAY', label: 'Working Day',
        workingDayConfig: {
          startTime: '08:30', startGraceMinutes: 5,
          endTime: '13:00',   endGraceMinutes: 5,
          punchInFrom: '07:30', punchInTo: '09:30',
          punchOutFrom: '12:00', punchOutTo: '14:00', spanDays: 2
        },
        otRules: [],
        breaks: [],
        attendancePolicies: []
      },
      { typeKey: 'OFF_DAY', label: 'Off Day', otRules: [], breaks: [], attendancePolicies: [] },
      { typeKey: 'HOLIDAY', label: 'Holiday', otRules: HOLIDAY_OT(), breaks: [], attendancePolicies: [] }
    ],
    blocks: [{ id: '4', sequence: 1, startTime: '08:30', endTime: '13:00', startGraceMinutes: 5, endGraceMinutes: 5 }],
    breaks: [],
    otRules: [],
    createdAt: new Date().toISOString(),
    legalEntities: [{ code: 'C003', name: 'Online Accounting', type: 'company', country: 'United Kingdom' }]
  },

  {
    id: '5',
    name: 'Flexible Office',
    type: 'FLEXIBLE',
    isMultiDay: false,
    crossesMidnight: false,
    weekSchedule: MON_FRI,
    dayTypeConfigs: [
      {
        typeKey: 'WORKING_DAY', label: 'Working Day',
        workingDayConfig: {
          startTime: '07:00', startGraceMinutes: 0,
          endTime: '20:00',   endGraceMinutes: 0,
          punchInFrom: '07:00', punchInTo: '20:00',
          punchOutFrom: '07:00', punchOutTo: '20:00',
          minWorkingHours: 8, spanDays: 2
        },
        otRules: WD_OT('Flexible OT', 8, '1.5', '2.0'),
        breaks: [],
        attendancePolicies: []
      },
      { typeKey: 'OFF_DAY',  label: 'Off Day',  otRules: OFF_OT('2.0'),  breaks: [], attendancePolicies: [] },
      { typeKey: 'HOLIDAY',  label: 'Holiday',  otRules: HOLIDAY_OT(),   breaks: [], attendancePolicies: [] }
    ],
    blocks: [],
    breaks: [],
    otRules: [],
    windowStart: '07:00',
    windowEnd: '20:00',
    minWorkingHours: 8,
    createdAt: new Date().toISOString(),
    legalEntities: [{ code: 'C003', name: 'Online Accounting', type: 'company', country: 'United Kingdom' }]
  },

  {
    id: '6a',
    name: 'Multi-Holiday Roster',
    type: 'FIXED',
    isMultiDay: false,
    crossesMidnight: false,
    specialDayTypes: [
      { key: 'poya_day',    label: 'Poya Day',      predefinedHolidays: ['Poya Day'] },
      { key: 'christmas',   label: 'Christmas Day',  predefinedHolidays: ['Christmas Day'] }
    ],
    dayTypeConfigs: [
      {
        typeKey: 'WORKING_DAY', label: 'Working Day',
        workingDayConfig: {
          startTime: '08:30', startGraceMinutes: 10,
          endTime: '17:30',   endGraceMinutes: 10,
          punchInFrom: '07:30', punchInTo: '09:30',
          punchOutFrom: '16:30', punchOutTo: '18:30', spanDays: 2
        },
        otRules: WD_OT('Standard OT', 9, '1.5', '2.0'),
        breaks: [{ name: 'Lunch', isPaid: false, durationMinutes: 60, startTime: '12:30', endTime: '13:30' }],
        attendancePolicies: LATE_POLICY()
      },
      { typeKey: 'OFF_DAY', label: 'Off Day', otRules: OFF_OT('2.0'), breaks: [], attendancePolicies: [] },
      { typeKey: 'HOLIDAY', label: 'Holiday', otRules: HOLIDAY_OT(), breaks: [], attendancePolicies: [] },
      {
        typeKey: 'poya_day', label: 'Poya Day',
        workingDayConfig: {
          startTime: '08:30', startGraceMinutes: 5,
          endTime: '13:00',   endGraceMinutes: 5,
          punchInFrom: '07:30', punchInTo: '09:30',
          punchOutFrom: '12:00', punchOutTo: '14:00', spanDays: 2
        },
        otRules: [{
          name: 'Poya OT',
          type: 'PRE_APPROVAL' as const,
          standardHoursThreshold: 0,
          thresholdPeriod: 'DAILY' as const,
          otLimitHours: 5,
          otLimitPeriod: 'DAILY' as const,
          minimumOTMinutes: 60,
          roundingMinutes: 30,
          deductShortfall: false,
          shortfallDeductionCap: null,
          timeTiers: [{ periodType: 'FLAT' as const, hours: 0, fromHours: 0, toHours: 0, label: 'All OT Hours', multiplier: '2.5' }]
        }],
        breaks: [],
        attendancePolicies: []
      },
      {
        typeKey: 'christmas', label: 'Christmas Day',
        otRules: [{
          name: 'Christmas OT',
          type: 'PRE_APPROVAL' as const,
          standardHoursThreshold: 0,
          thresholdPeriod: 'DAILY' as const,
          otLimitHours: 8,
          otLimitPeriod: 'DAILY' as const,
          minimumOTMinutes: 60,
          roundingMinutes: 30,
          deductShortfall: false,
          shortfallDeductionCap: null,
          timeTiers: [{ periodType: 'FLAT' as const, hours: 0, fromHours: 0, toHours: 0, label: 'All OT Hours', multiplier: '3.0' }]
        }],
        breaks: [],
        attendancePolicies: []
      }
    ],
    blocks: [{ id: '6a', sequence: 1, startTime: '08:30', endTime: '17:30', startGraceMinutes: 10, endGraceMinutes: 10 }],
    breaks: [{ id: '6ab', name: 'Lunch', isPaid: false, durationMinutes: 60, startTime: '12:30', endTime: '13:30' }],
    otRules: [],
    createdAt: new Date().toISOString()
  },

  {
    id: '6b',
    name: 'Poya Half-Day',
    type: 'FIXED',
    isMultiDay: false,
    crossesMidnight: false,
    specialDayTypes: [
      { key: 'poya_day', label: 'Poya Day', predefinedHolidays: ['Poya Day', 'Hadji Festival'] }
    ],
    dayTypeConfigs: [
      {
        typeKey: 'WORKING_DAY', label: 'Working Day',
        workingDayConfig: {
          startTime: '08:00', startGraceMinutes: 5,
          endTime: '17:00',   endGraceMinutes: 5,
          punchInFrom: '07:00', punchInTo: '09:00',
          punchOutFrom: '16:00', punchOutTo: '18:00', spanDays: 2
        },
        otRules: WD_OT('Standard OT', 9, '1.5', '2.0'),
        breaks: [{ name: 'Lunch', isPaid: false, durationMinutes: 45, startTime: '13:00', endTime: '13:45' }],
        attendancePolicies: []
      },
      { typeKey: 'OFF_DAY', label: 'Off Day', otRules: OFF_OT('2.0'), breaks: [], attendancePolicies: [] },
      { typeKey: 'HOLIDAY', label: 'Holiday', otRules: HOLIDAY_OT(), breaks: [], attendancePolicies: [] },
      {
        typeKey: 'poya_day', label: 'Poya Day',
        workingDayConfig: {
          startTime: '08:00', startGraceMinutes: 5,
          endTime: '12:00',   endGraceMinutes: 5,
          punchInFrom: '07:00', punchInTo: '09:00',
          punchOutFrom: '11:00', punchOutTo: '13:00', spanDays: 2
        },
        otRules: [{
          name: 'Poya OT',
          type: 'AUTO_CALCULATE' as const,
          standardHoursThreshold: 4,
          thresholdPeriod: 'DAILY' as const,
          otLimitHours: 4,
          otLimitPeriod: 'DAILY' as const,
          minimumOTMinutes: 30,
          roundingMinutes: 30,
          deductShortfall: false,
          shortfallDeductionCap: null,
          timeTiers: [{ periodType: 'FLAT' as const, hours: 0, fromHours: 0, toHours: 0, label: 'All OT Hours', multiplier: '2.5' }]
        }],
        breaks: [],
        attendancePolicies: []
      }
    ],
    blocks: [{ id: '6b1', sequence: 1, startTime: '08:00', endTime: '17:00', startGraceMinutes: 5, endGraceMinutes: 5 }],
    breaks: [{ id: '6b2', name: 'Lunch', isPaid: false, durationMinutes: 45, startTime: '13:00', endTime: '13:45' }],
    otRules: [],
    createdAt: new Date().toISOString(),
    legalEntities: [{ code: 'C004', name: 'Online Solutions', type: 'company', country: 'Sri Lanka' }]
  },

  {
    id: '6',
    name: 'EST Remote Shift',
    type: 'FIXED',
    isMultiDay: true,
    crossesMidnight: true,
    weekSchedule: MON_FRI,
    dayTypeConfigs: [
      {
        typeKey: 'WORKING_DAY', label: 'Working Day',
        workingDayConfig: {
          startTime: '18:30', startGraceMinutes: 15,
          endTime: '03:30',   endGraceMinutes: 15,
          punchInFrom: '17:30', punchInTo: '19:30',
          punchOutFrom: '02:30', punchOutTo: '04:30', spanDays: 2
        },
        otRules: WD_OT('Remote OT', 9, '1.5', '2.0'),
        breaks: [{ name: 'Dinner Break', isPaid: false, durationMinutes: 30, startTime: '21:00', endTime: '21:30' }],
        attendancePolicies: LATE_POLICY()
      },
      { typeKey: 'OFF_DAY',  label: 'Off Day',  otRules: OFF_OT('2.0'),  breaks: [], attendancePolicies: [] },
      { typeKey: 'HOLIDAY',  label: 'Holiday',  otRules: HOLIDAY_OT(),   breaks: [], attendancePolicies: [] }
    ],
    blocks: [{ id: '6', sequence: 1, startTime: '18:30', endTime: '03:30', startGraceMinutes: 15, endGraceMinutes: 15 }],
    breaks: [{ id: '6a', name: 'Dinner Break', isPaid: false, durationMinutes: 30, startTime: '21:00', endTime: '21:30' }],
    otRules: [],
    createdAt: new Date().toISOString(),
    legalEntities: [{ code: 'C004', name: 'Online Solutions', type: 'company', country: 'Sri Lanka' }]
  }
];

const MOCK_EMPLOYEES = [
  // Arrow SL - Engineering - Software Dev Line 1
  { id: 'e1', employeeId: 'e1', first_name: 'Aisha', last_name: 'Khan', full_name: 'Aisha Khan', employeeName: 'Aisha Khan', designation: 'Customer Support', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Customer Support', line: 'Support', status: 'Active' },
  { id: 'e9', employeeId: 'e9', first_name: 'Kasun', last_name: 'Perera', full_name: 'Kasun Perera', employeeName: 'Kasun Perera', designation: 'Software Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Software Dev Line 1', status: 'Active' },
  { id: 'e10', employeeId: 'e10', first_name: 'Minidu', last_name: 'Liyanage', full_name: 'Minidu Liyanage', employeeName: 'Minidu Liyanage', designation: 'Software Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Software Dev Line 1', status: 'Active' },
  { id: 'e11', employeeId: 'e11', first_name: 'Dilshan', last_name: 'Senanayake', full_name: 'Dilshan Senanayake', employeeName: 'Dilshan Senanayake', designation: 'Software Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Software Dev Line 1', status: 'Active' },

  // Arrow SL - Engineering - QA Line
  { id: 'e2', employeeId: 'e2', first_name: 'Bnar', last_name: 'Ali', full_name: 'Bnar Ali', employeeName: 'Bnar Ali', designation: 'Customer Support', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Customer Support', line: 'Support', status: 'Active' },
  { id: 'e12', employeeId: 'e12', first_name: 'Anjali', last_name: 'De Silva', full_name: 'Anjali De Silva', employeeName: 'Anjali De Silva', designation: 'QA Tester', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'QA Line', status: 'Active' },
  { id: 'e13', employeeId: 'e13', first_name: 'Suraj', last_name: 'Fernando', full_name: 'Suraj Fernando', employeeName: 'Suraj Fernando', designation: 'QA Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'QA Line', status: 'Active' },

  // Arrow SL - Engineering - Cloud Infrastructure
  { id: 'e3', employeeId: 'e3', first_name: 'Saman', last_name: 'Perera', full_name: 'Saman Perera', employeeName: 'Saman Perera', designation: 'Software Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Software Dev Line 1', status: 'Active' },
  { id: 'e14', employeeId: 'e14', first_name: 'Prabath', last_name: 'Kumara', full_name: 'Prabath Kumara', employeeName: 'Prabath Kumara', designation: 'Cloud Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Cloud Infrastructure', status: 'Active' },

  // Arrow US - Product Management - Product Line A
  { id: 'e4', employeeId: 'e4', first_name: 'Dev', last_name: 'Silva', full_name: 'Dev Silva', employeeName: 'Dev Silva', designation: 'Software Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Software Dev Line 1', status: 'Active' },
  { id: 'e15', employeeId: 'e15', first_name: 'Nuwan', last_name: 'Wickramasinghe', full_name: 'Nuwan Wickramasinghe', employeeName: 'Nuwan Wickramasinghe', designation: 'Product Owner', tenant: 'Arrow Group', company: 'Arrow US', department: 'Product Management', line: 'Product Line A', status: 'Active' },

  // Arrow US - Product Management - Design Line
  { id: 'e5', employeeId: 'e5', first_name: 'Nadia', last_name: 'Hassan', full_name: 'Nadia Hassan', employeeName: 'Nadia Hassan', designation: 'HR Specialist', tenant: 'Arrow Group', company: 'Arrow SL', department: 'HR', line: 'People Operations', status: 'Active' },
  { id: 'e16', employeeId: 'e16', first_name: 'Hansani', last_name: 'Ranasinghe', full_name: 'Hansani Ranasinghe', employeeName: 'Hansani Ranasinghe', designation: 'UI Designer', tenant: 'Arrow Group', company: 'Arrow US', department: 'Product Management', line: 'Design Line', status: 'Active' },

  // Beta Holdings - Beta Foods - Operations - Logistics Line
  { id: 'e6', employeeId: 'e6', first_name: 'Ruwan', last_name: 'Jay', full_name: 'Ruwan Jay', employeeName: 'Ruwan Jay', designation: 'Security Officer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Security', line: 'Facilities Security', status: 'Active' },
  { id: 'e17', employeeId: 'e17', first_name: 'Roshan', last_name: 'Silva', full_name: 'Roshan Silva', employeeName: 'Roshan Silva', designation: 'Logistics Coordinator', tenant: 'Beta Holdings', company: 'Beta Foods', department: 'Operations', line: 'Logistics Line', status: 'Active' },

  // Beta Holdings - Beta Foods - Operations - Security Line
  { id: 'e7', employeeId: 'e7', first_name: 'Tariq', last_name: 'Noor', full_name: 'Tariq Noor', employeeName: 'Tariq Noor', designation: 'Customer Support', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Customer Support', line: 'Support', status: 'Active' },
  { id: 'e18', employeeId: 'e18', first_name: 'Sumith', last_name: 'Bandara', full_name: 'Sumith Bandara', employeeName: 'Sumith Bandara', designation: 'Security Guard', tenant: 'Beta Holdings', company: 'Beta Foods', department: 'Operations', line: 'Security Line', status: 'Active' },

  // Beta Holdings - Beta Finance - Finance - Accounting Line
  { id: 'e8', employeeId: 'e8', first_name: 'Imran', last_name: 'Cassim', full_name: 'Imran Cassim', employeeName: 'Imran Cassim', designation: 'Software Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Software Dev Line 1', status: 'Active' },
  { id: 'e19', employeeId: 'e19', first_name: 'Janaka', last_name: 'Prasad', full_name: 'Janaka Prasad', employeeName: 'Janaka Prasad', designation: 'Accountant', tenant: 'Beta Holdings', company: 'Beta Finance', department: 'Finance', line: 'Accounting Line', status: 'Active' },
  { id: 'e100', employeeId: 'e100', first_name: 'John', last_name: 'Doe', full_name: 'John Doe', employeeName: 'John Doe', designation: 'Intern', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Software Dev Line 1', status: 'Inactive' }
];

const MOCK_GROUPS: ShiftGroup[] = [
  {
    id: '1',
    name: 'IT Team Sri Lanka',
    type: 'TEAM',
    location: 'Colombo',
    memberCount: 3,
    membershipType: 'STATIC',
    members: [
      { employeeId: 'e1', employeeName: 'Amal Perera',    designation: 'Software Engineer', tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Software Dev Line 1' },
      { employeeId: 'e2', employeeName: 'Nimali Silva',   designation: 'QA Engineer',       tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'QA Line' },
      { employeeId: 'e3', employeeName: 'Ruwan Fernando', designation: 'DevOps Engineer',   tenant: 'Arrow Group', company: 'Arrow SL', department: 'Engineering', line: 'Cloud Infrastructure' }
    ],
    createdAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Factory Floor Shift A',
    type: 'ROTATION_COHORT',
    location: 'Ratmalana',
    memberCount: 2,
    membershipType: 'STATIC',
    members: [
      { employeeId: 'e7', employeeName: 'Pradeep Rathnayake', designation: 'Security Analyst',  tenant: 'Beta Holdings', company: 'Beta Foods', department: 'Operations', line: 'Security Line' },
      { employeeId: 'e8', employeeName: 'Dilini Wickramasinghe', designation: 'HR Executive',      tenant: 'Beta Holdings', company: 'Beta Finance', department: 'Finance', line: 'Accounting Line' }
    ],
    createdAt: new Date().toISOString()
  }
];

let assignmentIdSeq = Date.now() + 3;
const nextAssignmentId = () => `sa_${assignmentIdSeq++}`;

const mem = (employeeId: string): ShiftAssignmentMember => {
  const e = MOCK_EMPLOYEES.find(x => x.employeeId === employeeId)!;
  return { employeeId, employeeName: e.employeeName ?? e.full_name, designation: e.designation ?? '', department: e.department ?? '' };
};

// Six named assignment groups for June 2026 — Active, Scheduled, and Completed relative to 2026-06-06
const MOCK_SHIFT_ASSIGNMENTS: ShiftAssignment[] = [
  // ── Active — Normal ───────────────────────────────────────────────────────
  {
    id: 'sa_1', type: 'NORMAL',
    name: 'IT Morning Shift – June 2026',
    shiftId: '1', shiftName: 'IT Standard',
    startDate: '2026-06-01', endDate: '2026-06-15',
    memberCount: 5, members: ['e1', 'e2', 'e3', 'e9', 'e10'].map(mem),
    createdAt: '2026-05-28T08:00:00.000Z'
  },
  {
    id: 'sa_2', type: 'NORMAL',
    name: 'Factory Night Crew – June',
    shiftId: '3', shiftName: 'Factory Night',
    startDate: '2026-06-01', endDate: '2026-06-30',
    memberCount: 4, members: ['e7', 'e18', 'e8', 'e19'].map(mem),
    createdAt: '2026-05-29T10:00:00.000Z'
  },
  // ── Scheduled — Normal ────────────────────────────────────────────────────
  {
    id: 'sa_3', type: 'NORMAL',
    name: 'Remote Product Team – Mid June',
    shiftId: '6', shiftName: 'EST Remote Shift',
    startDate: '2026-06-09', endDate: '2026-06-23',
    memberCount: 4, members: ['e4', 'e5', 'e15', 'e16'].map(mem),
    createdAt: '2026-06-05T09:00:00.000Z'
  },
  {
    id: 'sa_4', type: 'NORMAL',
    name: 'QA & DevOps Flex Coverage',
    shiftId: '5', shiftName: 'Flexible Office',
    startDate: '2026-06-10', endDate: '2026-06-28',
    memberCount: 3, members: ['e12', 'e13', 'e14'].map(mem),
    createdAt: '2026-06-05T12:00:00.000Z'
  },
  // ── Completed — Normal ────────────────────────────────────────────────────
  {
    id: 'sa_5', type: 'NORMAL',
    name: 'Factory Morning – May Close',
    shiftId: '2', shiftName: 'Factory Morning',
    startDate: '2026-05-20', endDate: '2026-06-02',
    memberCount: 2, members: ['e6', 'e17'].map(mem),
    createdAt: '2026-05-18T10:00:00.000Z'
  },
  {
    id: 'sa_6', type: 'NORMAL',
    name: 'Saturday IT Support – May',
    shiftId: '4', shiftName: 'Saturday Half-Day',
    startDate: '2026-05-16', endDate: '2026-05-30',
    memberCount: 3, members: ['e2', 'e3', 'e11'].map(mem),
    createdAt: '2026-05-15T08:00:00.000Z'
  },
  // ── Scheduled — Rotation ──────────────────────────────────────────────────
  {
    id: 'sa_7', type: 'ROTATION',
    name: 'Night Operations Rotation',
    startDate: '2026-06-08', endDate: '2026-08-30',
    intervalValue: 1, intervalUnit: 'WEEKS',
    cycles: [
      { id: 'rc1', label: 'Cycle 1' },
      { id: 'rc2', label: 'Cycle 2' },
      { id: 'rc3', label: 'Cycle 3' }
    ],
    rotationGroups: [
      { id: 'rg1', groupId: '1', groupName: 'IT Team – Sri Lanka',    headcount: 3, memberIds: ['e1','e2','e3'],  cycleShiftMap: { rc1: '2', rc2: '3', rc3: '1' } },
      { id: 'rg2', groupId: '2', groupName: 'Factory Floor – Shift A', headcount: 2, memberIds: ['e7','e8'],       cycleShiftMap: { rc1: '3', rc2: '1', rc3: '2' } }
    ],
    memberCount: 5,
    members: ['e1','e2','e3','e7','e8'].map(mem),
    createdAt: '2026-06-05T08:00:00.000Z'
  },
  {
    id: 'sa_8', type: 'ROTATION',
    name: 'Monthly Product Rotation',
    startDate: '2026-06-10', endDate: '2026-09-10',
    intervalValue: 1, intervalUnit: 'MONTHS',
    cycles: [
      { id: 'rc4', label: 'Cycle 1' },
      { id: 'rc5', label: 'Cycle 2' }
    ],
    rotationGroups: [
      { id: 'rg3', groupId: '', groupName: 'Product Team',  headcount: 3, memberIds: ['e4','e5','e15'],  cycleShiftMap: { rc4: '5', rc5: '6' } },
      { id: 'rg4', groupId: '', groupName: 'Design Team',   headcount: 2, memberIds: ['e16','e14'],      cycleShiftMap: { rc4: '6', rc5: '5' } }
    ],
    memberCount: 5,
    members: ['e4','e5','e15','e16','e14'].map(mem),
    createdAt: '2026-06-05T10:00:00.000Z'
  }
];

const MOCK_ATTENDANCE_LOGS: any[] = [
  // 2026-06-05 records (matches the mockup exactly)
  { id: 'al_05_1', employeeId: 'e1', employeeName: 'Aisha Khan', date: '2026-06-05', checkIn: '05:58', checkOut: '14:12', workHours: 8.23, status: 'Present', workHoursFormatted: '8h 00m', lateMinutes: '—', otHoursFormatted: '0h' },
  { id: 'al_05_2', employeeId: 'e2', employeeName: 'Bnar Ali', date: '2026-06-05', checkIn: '06:22', checkOut: '14:05', workHours: 7.72, status: 'Late', workHoursFormatted: '7h 30m', lateMinutes: '22m', otHoursFormatted: '0h' },
  { id: 'al_05_3', employeeId: 'e3', employeeName: 'Saman Perera', date: '2026-06-05', checkIn: '06:01', checkOut: '', workHours: null, status: 'Missing Out-punch', workHoursFormatted: '—', lateMinutes: '—', otHoursFormatted: '—' },
  { id: 'al_05_4', employeeId: 'e4', employeeName: 'Dev Silva', date: '2026-06-05', checkIn: '', checkOut: '', workHours: null, status: 'On Leave', workHoursFormatted: '—', lateMinutes: '—', otHoursFormatted: '—' },
  { id: 'al_05_5', employeeId: 'e5', employeeName: 'Nadia Hassan', date: '2026-06-05', checkIn: '', checkOut: '', workHours: null, status: 'WFH', workHoursFormatted: 'Remote', lateMinutes: '—', otHoursFormatted: '—' },
  { id: 'al_05_6', employeeId: 'e6', employeeName: 'Ruwan Jay', date: '2026-06-05', checkIn: '22:54', checkOut: '08:30', workHours: 9.6, status: 'Present', workHoursFormatted: '8h 00m', lateMinutes: '—', otHoursFormatted: '1h 30m', shiftName: 'Night 23-07', dayType: 'Working +1d' },
  { id: 'al_05_7', employeeId: 'e7', employeeName: 'Tariq Noor', date: '2026-06-05', checkIn: '08:40', checkOut: '14:00', workHours: 5.33, status: 'Half Day', workHoursFormatted: '4h 50m', lateMinutes: '—', otHoursFormatted: '0h' },
  { id: 'al_05_8', employeeId: 'e8', employeeName: 'Imran Cassim', date: '2026-06-05', checkIn: '', checkOut: '', workHours: null, status: 'Absent', workHoursFormatted: '—', lateMinutes: '—', otHoursFormatted: '—' },

  // 2026-06-08 records (today)
  { id: 'al_08_1', employeeId: 'e1',  employeeName: 'Aisha Khan',     date: '2026-06-08', checkIn: '08:12', checkOut: '17:28', workHours: 9.27,  status: 'Present' },
  { id: 'al_08_2', employeeId: 'e2',  employeeName: 'Bnar Ali',       date: '2026-06-08', checkIn: '08:47', checkOut: '17:45', workHours: 8.97,  status: 'Present' },
  { id: 'al_08_3', employeeId: 'e3',  employeeName: 'Saman Perera',   date: '2026-06-08', checkIn: '09:22', checkOut: '18:05', workHours: 8.72,  status: 'Late' },
  { id: 'al_08_4', employeeId: 'e4',  employeeName: 'Dev Silva',      date: '2026-06-08', checkIn: '',      checkOut: '',      workHours: null,  status: 'Absent' },
  { id: 'al_08_5', employeeId: 'e5',  employeeName: 'Nadia Hassan',   date: '2026-06-08', checkIn: '',      checkOut: '',      workHours: null,  status: 'WFH' },
  { id: 'al_08_6', employeeId: 'e6',  employeeName: 'Ruwan Jay',      date: '2026-06-08', checkIn: '22:58', checkOut: '',      workHours: null,  status: 'Missing Out-punch', shiftName: 'Night 23-07', dayType: 'Working +1d' },
  { id: 'al_08_7', employeeId: 'e7',  employeeName: 'Tariq Noor',     date: '2026-06-08', checkIn: '08:33', checkOut: '17:30', workHours: 8.95,  status: 'Present' },
  { id: 'al_08_8', employeeId: 'e8',  employeeName: 'Imran Cassim',   date: '2026-06-08', checkIn: '',      checkOut: '',      workHours: null,  status: 'On Leave' },

  // Seed other dates for reports with consistent names
  { id: 'al_1', employeeId: 'e1', employeeName: 'Aisha Khan', date: '2026-06-01', checkIn: '08:15', checkOut: '17:30', workHours: 9.25, status: 'Present' },
  { id: 'al_2', employeeId: 'e2', employeeName: 'Bnar Ali', date: '2026-06-01', checkIn: '09:15', checkOut: '17:30', workHours: 8.25, status: 'Late' },
  { id: 'al_3', employeeId: 'e3', employeeName: 'Saman Perera', date: '2026-06-01', checkIn: '08:30', checkOut: '', workHours: null, status: 'Missing Out-punch' },
  
  { id: 'al_4', employeeId: 'e1', employeeName: 'Aisha Khan', date: '2026-06-02', checkIn: '08:20', checkOut: '17:45', workHours: 9.42, status: 'Present' },
  { id: 'al_5', employeeId: 'e2', employeeName: 'Bnar Ali', date: '2026-06-02', checkIn: '08:30', checkOut: '17:30', workHours: 9.0, status: 'Present' },
  { id: 'al_6', employeeId: 'e3', employeeName: 'Saman Perera', date: '2026-06-02', checkIn: '09:45', checkOut: '18:00', workHours: 8.25, status: 'Late' },

  { id: 'al_7', employeeId: 'e1', employeeName: 'Aisha Khan', date: '2026-06-03', checkIn: '08:25', checkOut: '17:30', workHours: 9.08, status: 'Present' },
  { id: 'al_8', employeeId: 'e2', employeeName: 'Bnar Ali', date: '2026-06-03', checkIn: '08:30', checkOut: '17:30', workHours: 9.0, status: 'Present' },
  { id: 'al_9', employeeId: 'e3', employeeName: 'Saman Perera', date: '2026-06-03', checkIn: '08:30', checkOut: '17:00', workHours: 8.5, status: 'Present' },

  { id: 'al_10', employeeId: 'e1', employeeName: 'Aisha Khan', date: '2026-06-04', checkIn: '08:20', checkOut: '17:30', workHours: 9.17, status: 'Present' },
  { id: 'al_11', employeeId: 'e2', employeeName: 'Bnar Ali', date: '2026-06-04', checkIn: '08:25', checkOut: '17:30', workHours: 9.08, status: 'Present' },
  { id: 'al_12', employeeId: 'e3', employeeName: 'Saman Perera', date: '2026-06-04', checkIn: '09:05', checkOut: '17:30', workHours: 8.42, status: 'Late' }
];

// Current logged-in employee for "my" endpoints
const CURRENT_EMPLOYEE_ID = 'e1';


let correctionIdSeq = Date.now() + 10;
const nextCorrectionId = () => `cr_${correctionIdSeq++}`;

let MOCK_CORRECTION_REQUESTS: AttendanceCorrectionRequest[] = [
  {
    id: 'cr_1',
    employeeId: 'e1', employeeName: 'Aisha Khan', department: 'Engineering', designation: 'Senior Software Engineer',
    attendanceDate: '2026-06-01', shiftName: 'IT Standard',
    correctionType: 'MISSING_OUT_PUNCH',
    originalInTime: '08:15', originalOutTime: undefined,
    requestedInTime: undefined, requestedOutTime: '17:30',
    reason: 'Forgot to punch out — left office at 17:30 as usual.',
    status: 'APPROVED',
    submittedAt: '2026-06-02T09:00:00.000Z',
    reviewedAt: '2026-06-02T11:30:00.000Z',
    reviewedBy: 'Michael Chen',
    reviewerComments: 'Verified with office security logs. Approved.'
  },
  {
    id: 'cr_2',
    employeeId: 'e1', employeeName: 'Aisha Khan', department: 'Engineering', designation: 'Senior Software Engineer',
    attendanceDate: '2026-06-03', shiftName: 'IT Standard',
    correctionType: 'WRONG_IN_TIME',
    originalInTime: '09:45', originalOutTime: '17:30',
    requestedInTime: '08:30', requestedOutTime: undefined,
    reason: 'Biometric scanner malfunctioned — had to use backup card reader which logged a wrong time.',
    status: 'PENDING',
    submittedAt: '2026-06-04T08:30:00.000Z'
  },
  {
    id: 'cr_3',
    employeeId: 'e1', employeeName: 'Aisha Khan', department: 'Engineering', designation: 'Senior Software Engineer',
    attendanceDate: '2026-05-28', shiftName: 'IT Standard',
    correctionType: 'BOTH_PUNCHES',
    originalInTime: undefined, originalOutTime: undefined,
    requestedInTime: '08:20', requestedOutTime: '17:00',
    reason: 'System was down for maintenance on this day. Physical logbook confirms attendance.',
    status: 'REJECTED',
    submittedAt: '2026-05-29T10:00:00.000Z',
    reviewedAt: '2026-05-30T09:00:00.000Z',
    reviewedBy: 'Michael Chen',
    reviewerComments: 'No supporting evidence provided. Please resubmit with security gate log.'
  },
  {
    id: 'cr_4',
    employeeId: 'e2', employeeName: 'Bnar Ali', department: 'Engineering', designation: 'Software Engineer',
    attendanceDate: '2026-06-02', shiftName: 'IT Standard',
    correctionType: 'MISSING_IN_PUNCH',
    originalInTime: undefined, originalOutTime: '17:30',
    requestedInTime: '08:30', requestedOutTime: undefined,
    reason: 'Entrance card reader was offline when I arrived. IT confirmed the outage.',
    status: 'PENDING',
    submittedAt: '2026-06-03T08:45:00.000Z'
  },
  {
    id: 'cr_5',
    employeeId: 'e3', employeeName: 'Saman Perera', department: 'QA', designation: 'QA Engineer',
    attendanceDate: '2026-06-01', shiftName: 'IT Standard',
    correctionType: 'MISSING_OUT_PUNCH',
    originalInTime: '08:30', originalOutTime: undefined,
    requestedInTime: undefined, requestedOutTime: '18:00',
    reason: 'Left through emergency exit during fire drill — exit reader was not active.',
    status: 'APPROVED',
    submittedAt: '2026-06-02T08:00:00.000Z',
    reviewedAt: '2026-06-02T14:00:00.000Z',
    reviewedBy: 'Michael Chen',
    reviewerComments: 'Fire drill confirmed. Approved.'
  },
  {
    id: 'cr_6',
    employeeId: 'e5', employeeName: 'Nadia Hassan', department: 'Product', designation: 'Product Manager',
    attendanceDate: '2026-06-04', shiftName: 'Flexible Office',
    correctionType: 'WRONG_OUT_TIME',
    originalInTime: '09:00', originalOutTime: '14:00',
    requestedInTime: undefined, requestedOutTime: '18:30',
    reason: 'Was in an off-site client meeting until 18:30. Reader at client site logged checkout.',
    status: 'PENDING',
    submittedAt: '2026-06-05T09:30:00.000Z'
  },
  {
    id: 'cr_7',
    employeeId: 'e7', employeeName: 'Tariq Noor', department: 'Operations', designation: 'Operations Lead',
    attendanceDate: '2026-06-03', shiftName: 'Factory Morning',
    correctionType: 'BOTH_PUNCHES',
    originalInTime: undefined, originalOutTime: undefined,
    requestedInTime: '07:00', requestedOutTime: '15:00',
    reason: 'Attended a warehouse audit — all punches done at warehouse, not registered in main system.',
    status: 'APPROVED',
    submittedAt: '2026-06-04T11:00:00.000Z',
    reviewedAt: '2026-06-05T10:00:00.000Z',
    reviewedBy: 'Sarah Johnson',
    reviewerComments: 'Warehouse attendance sheet verified. Approved.'
  },
  {
    id: 'cr_8',
    employeeId: 'e2', employeeName: 'Bnar Ali', department: 'Engineering', designation: 'Software Engineer',
    attendanceDate: '2026-06-05', shiftName: 'IT Standard',
    correctionType: 'WRONG_IN_TIME',
    originalInTime: '09:30', originalOutTime: '17:30',
    requestedInTime: '08:30', requestedOutTime: undefined,
    reason: 'Scanner logged wrong time — arrived at 8:30 but device showed 9:30 due to clock drift.',
    status: 'PENDING',
    submittedAt: '2026-06-06T08:00:00.000Z'
  },
  {
    id: 'cr_9',
    employeeId: 'e1', employeeName: 'Aisha Khan', department: 'Engineering', designation: 'Senior Software Engineer',
    attendanceDate: '2026-06-05', shiftName: 'IT Standard',
    correctionType: 'MISSING_OUT_PUNCH',
    originalInTime: '05:58', originalOutTime: undefined,
    requestedInTime: undefined, requestedOutTime: '14:12',
    reason: 'Checkout reader at main gate was offline. Checked out via security log.',
    status: 'CANCELLED',
    submittedAt: '2026-06-06T07:00:00.000Z'
  }
];

let calendarIdSeq = Date.now() + 2;
const nextCalendarId = () => `c_${calendarIdSeq++}`;

const MOCK_CALENDARS: HolidayCalendar[] = [
  {
    id: '1',
    name: 'Sri Lanka Public Holidays 2026',
    appliesTo: { scope: 'BY_COUNTRY' as any, countries: ['Sri Lanka'] },
    year: 2026,
    holidays: [
      {
        id: 'h1',
        name: 'Vesak Full Moon Poya Day',
        startDate: '2026-05-31',
        color: '#FF6B6B',
        appliesTo: { type: 'ALL', values: [] }
      },
      {
        id: 'h2',
        name: 'Sinhala & Tamil New Year Day',
        startDate: '2026-04-13',
        endDate: '2026-04-14',
        color: '#4DABF7',
        appliesTo: { type: 'ALL', values: [] }
      }
    ],
    createdAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Standard Public Holidays 2026',
    appliesTo: { scope: 'ALL' as any },
    year: 2026,
    holidays: [
      { id: 'h201', name: 'New Year\'s Day', startDate: '2026-01-01', color: '#FF6B6B', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h202', name: 'Tamil Thai Pongal Day', startDate: '2026-01-14', color: '#4DABF7', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h203', name: 'Duruthu Full Moon Poya Day', startDate: '2026-01-23', color: '#FCC419', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h204', name: 'National Day (Independence Day)', startDate: '2026-02-04', color: '#20C997', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h205', name: 'Navam Full Moon Poya Day', startDate: '2026-02-21', color: '#94D82D', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h206', name: 'Mahasivarathri Day', startDate: '2026-03-07', color: '#7289DA', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h207', name: 'Medin Full Moon Poya Day', startDate: '2026-03-22', color: '#EE5253', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h208', name: 'Good Friday', startDate: '2026-04-03', color: '#FF9F43', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h209', name: 'Bak Full Moon Poya Day', startDate: '2026-04-09', color: '#10AC84', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h210', name: 'Day before Sinhala & Tamil New Year', startDate: '2026-04-12', color: '#5758BB', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h211', name: 'Sinhala & Tamil New Year Day', startDate: '2026-04-13', endDate: '2026-04-14', color: '#4DABF7', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h212', name: 'May Day (Labour Day)', startDate: '2026-05-01', color: '#ED4C67', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h213', name: 'Vesak Full Moon Poya Day', startDate: '2026-05-31', color: '#FF6B6B', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h214', name: 'Day after Vesak Full Moon Poya Day', startDate: '2026-06-01', color: '#33D9B2', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h215', name: 'Poson Full Moon Poya Day', startDate: '2026-06-15', color: '#FFC312', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h216', name: 'Esala Full Moon Poya Day', startDate: '2026-07-28', color: '#C4E538', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h217', name: 'Nikini Full Moon Poya Day', startDate: '2026-08-27', color: '#12CBC4', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h218', name: 'Milad-Un-Nabi (Prophet\'s Birthday)', startDate: '2026-09-05', color: '#FDA7DF', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h219', name: 'Binara Full Moon Poya Day', startDate: '2026-09-26', color: '#ED4C67', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h220', name: 'Vap Full Moon Poya Day', startDate: '2026-10-25', color: '#38CC88', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h221', name: 'Deepavali Festival Day', startDate: '2026-11-08', color: '#FF9F43', appliesTo: { type: 'ALL', values: [] } },
      { id: 'h222', name: 'Christmas Day', startDate: '2026-12-25', color: '#EE5253', appliesTo: { type: 'ALL', values: [] } }
    ],
    createdAt: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Online Solutions Holidays 2026',
    appliesTo: { scope: 'BY_COMPANY' as any, nodes: [{ code: 'C004', name: 'Online Solutions', type: 'company', country: 'Sri Lanka' }] },
    year: 2026,
    holidays: [
      { id: 'h301', name: 'Company Foundation Day', startDate: '2026-06-10', color: '#7289DA', appliesTo: { type: 'ALL', values: [] } }
    ],
    createdAt: new Date().toISOString()
  }
];


// ---------------------------------------------------------------------------
// Interceptor
// ---------------------------------------------------------------------------

function ok(body: any) {
  return of(new HttpResponse({ status: 200, body }));
}

function created(body: any) {
  return of(new HttpResponse({ status: 201, body }));
}

function noContent() {
  return of(new HttpResponse({ status: 204, body: null }));
}

export const mockAttendanceApiInterceptor: HttpInterceptorFn = (req, next) => {
  const url = req.url;

  // Intercept the plain employees list lookup for the shift-group member picker with dummy
  // data. Anchored (not a substring match) so it doesn't also swallow sibling real-backend
  // endpoints like /employees/search (used by Benefits bulk enrolment).
  if (environment.authMode !== 'keycloak' && req.method === 'GET' && /\/employees(\?.*)?$/.test(url)) {
    // Allow bypass for real-backend calls (e.g. benefit service resolving employee names)
    if (req.context.get(BYPASS_MOCK)) return next(req);
    return ok(MOCK_EMPLOYEES);
  }

  const isAttendanceSvc = url.includes('attendancesvc') || url.includes('localhost:8085');
  if (!isAttendanceSvc) return next(req);

  // ─── Daily Attendance Logs (legacy endpoint) ──────────────────────────────

  // GET /attendance/logs
  if (req.method === 'GET' && (url.includes('/attendance/logs') || url.includes('/attendance/records'))) {
    const startDate = req.params.get('startDate');
    const endDate = req.params.get('endDate');
    const status = req.params.get('status');
    const employeeId = req.params.get('employeeId');

    let result = [...MOCK_ATTENDANCE_LOGS];

    if (startDate) {
      result = result.filter(log => log.date >= startDate);
    }
    if (endDate) {
      result = result.filter(log => log.date <= endDate);
    }
    if (status) {
      result = result.filter(log => log.status === status);
    }
    if (employeeId) {
      result = result.filter(log => log.employeeId === employeeId);
    }

    // Enrich logs with designation and profile picture lookup from MOCK_EMPLOYEES
    const enriched = result.map(log => {
      const emp: any = MOCK_EMPLOYEES.find(e => e.employeeId === log.employeeId);
      
      // Calculate worked hours formatted (e.g. 8h 00m)
      let workHoursFormatted = log.workHoursFormatted;
      if (!workHoursFormatted) {
        workHoursFormatted = '—';
        if (log.workHours != null) {
          const totalMin = Math.round(log.workHours * 60);
          const h = Math.floor(totalMin / 60);
          const m = totalMin % 60;
          workHoursFormatted = `${h}h ${String(m).padStart(2, '0')}m`;
        } else if (log.status === 'WFH') {
          workHoursFormatted = 'Remote';
        }
      }
 
      // Calculate Late minutes
      let lateMinutes = log.lateMinutes;
      if (!lateMinutes) {
        lateMinutes = '—';
        if (log.status === 'Late') {
          const isEST = emp?.employeeId === 'e6';
          const shiftStartStr = isEST ? '23:00' : '08:30';
          if (log.checkIn) {
            const [ciH, ciM] = log.checkIn.split(':').map(Number);
            const [sH, sM] = shiftStartStr.split(':').map(Number);
            const diff = (ciH * 60 + ciM) - (sH * 60 + sM);
            lateMinutes = diff > 0 ? `${diff}m` : '22m'; // fallback to 22m
          } else {
            lateMinutes = '22m';
          }
        }
      }
 
      // Calculate OT hours formatted
      let otHoursFormatted = log.otHoursFormatted;
      if (!otHoursFormatted) {
        otHoursFormatted = '—';
        if (log.status === 'Present' || log.status === 'Late') {
          const wh = log.workHours ?? 0;
          if (wh > 8) {
            const otDiff = wh - 8;
            const otMin = Math.round(otDiff * 60);
            const h = Math.floor(otMin / 60);
            const m = otMin % 60;
            otHoursFormatted = m > 0 ? `${h}h ${m}m` : `${h}h`;
          } else {
            otHoursFormatted = '0h';
          }
        }
      }
 
      return {
        ...log,
        designation: emp?.designation ?? '',
        avatar: emp?.profilePicture ?? emp?.avatar ?? emp?.profile_picture ?? '',
        department: emp?.department ?? 'Engineering',
        location: emp?.company === 'Arrow SL' ? 'Colombo' : (emp?.company === 'Arrow US' ? 'New York' : 'Colombo'),
        shiftName: log.shiftName ?? (emp?.employeeId === 'e6' ? 'Night 23-07' : 'Day 06-14'),
        dayType: log.dayType ?? (emp?.employeeId === 'e6' ? 'Working +1d' : 'Working'),
        workHoursFormatted,
        lateMinutes,
        otHoursFormatted
      };
    });

    // Sort by date desc, then by employeeId asc
    enriched.sort((a, b) => {
      if (a.date !== b.date) {
        return b.date.localeCompare(a.date);
      }
      return a.employeeId.localeCompare(b.employeeId);
    });

    return ok(enriched);
  }

  // PATCH /attendance/logs/:id  (admin direct edit)
  if (req.method === 'PATCH' && /\/attendance\/logs\/[\w-]+$/.test(url)) {
    const id   = url.split('/attendance/logs/')[1];
    const body = req.body as any;
    const checkIn  = (body.checkIn  ?? '').trim();
    const checkOut = (body.checkOut ?? '').trim();

    let workHours: number | null = null;
    if (checkIn && checkOut) {
      const [inH, inM]   = checkIn.split(':').map(Number);
      const [outH, outM] = checkOut.split(':').map(Number);
      workHours = Math.round(((outH * 60 + outM) - (inH * 60 + inM)) / 60 * 100) / 100;
    }
    let status = 'Present';
    if (checkIn && !checkOut) status = 'Missing Out-punch';
    else if (checkIn) {
      const [inH, inM] = checkIn.split(':').map(Number);
      status = (inH * 60 + inM) > (9 * 60) ? 'Late' : 'Present';
    }

    const totalMin = workHours ? Math.round(workHours * 60) : 0;
    const workHoursFormatted = workHours && workHours > 0
      ? `${Math.floor(totalMin / 60)}h ${String(totalMin % 60).padStart(2, '0')}m`
      : undefined;

    // Try legacy MOCK_ATTENDANCE_LOGS first, then synthesise a response
    const idx = MOCK_ATTENDANCE_LOGS.findIndex(l => l.id === id);
    if (idx >= 0) {
      MOCK_ATTENDANCE_LOGS[idx] = { ...MOCK_ATTENDANCE_LOGS[idx], checkIn, checkOut, workHours, status, workHoursFormatted, remarks: body.remarks ?? '' };
      return ok(MOCK_ATTENDANCE_LOGS[idx]);
    }
    // For records that came from MOCK_RECORDS_BY_DATE (id = "employeeId-date"),
    // return a synthesised updated record — the state service merges it into the signal
    return ok({ id, checkIn, checkOut, workHours, workHoursFormatted, status, remarks: body.remarks ?? '' });
  }

  // PUT /attendance/records (admin direct edit)
  if (req.method === 'PUT' && url.includes('/attendance/records')) {
    const body = req.body as any;
    const empId = body.employeeId;
    const date = body.date;
    const remarks = body.remarks ?? '';

    // Convert ISO strings in checkInTime/checkOutTime back to HH:mm for the mock state
    let checkIn: string | undefined;
    let checkOut: string | undefined;

    if (body.checkInTime) {
      const d = new Date(body.checkInTime);
      checkIn = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    }
    if (body.checkOutTime) {
      const d = new Date(body.checkOutTime);
      checkOut = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    }

    let workHours: number | null = null;
    if (checkIn && checkOut) {
      const [inH, inM]   = checkIn.split(':').map(Number);
      const [outH, outM] = checkOut.split(':').map(Number);
      workHours = Math.round(((outH * 60 + outM) - (inH * 60 + inM)) / 60 * 100) / 100;
    }
    let status = 'Present';
    if (checkIn && !checkOut) status = 'Missing Out-punch';
    else if (checkIn) {
      const [inH, inM] = checkIn.split(':').map(Number);
      status = (inH * 60 + inM) > (9 * 60) ? 'Late' : 'Present';
    }

    const totalMin = workHours ? Math.round(workHours * 60) : 0;
    const workHoursFormatted = workHours && workHours > 0
      ? `${Math.floor(totalMin / 60)}h ${String(totalMin % 60).padStart(2, '0')}m`
      : undefined;

    const idx = MOCK_ATTENDANCE_LOGS.findIndex(l => l.employeeId === empId && l.date === date);
    if (idx >= 0) {
      MOCK_ATTENDANCE_LOGS[idx] = {
        ...MOCK_ATTENDANCE_LOGS[idx],
        ...(checkIn ? { checkIn } : {}),
        ...(checkOut ? { checkOut } : {}),
        workHours,
        status,
        workHoursFormatted,
        remarks
      };
      return ok(MOCK_ATTENDANCE_LOGS[idx]);
    }

    const id = `${empId}-${date}`;
    return ok({ id, checkIn, checkOut, workHours, workHoursFormatted, status, remarks });
  }

  // POST /attendance/logs/raw-punches/validate
  if (req.method === 'POST' && url.includes('/attendance/logs/raw-punches/validate')) {
    const rows = req.body as any[];
    const validationResults: any[] = [];
    const validRows: any[] = [];
    const seenInFile = new Set<string>();

    rows.forEach((row, index) => {
      const rowNum = index + 2;
      const empId     = (row.employeeId  || row.employee_id  || '').trim();
      const dateStr   = (row.date        || '').trim();
      const punchTime = (row.punchTime   || row.punch_time   || '').trim();

      if (!empId) {
        validationResults.push({ row: rowNum, field: 'employeeId', message: 'Employee ID is required.', type: 'error' });
        return;
      }
      if (!dateStr) {
        validationResults.push({ row: rowNum, field: 'date', message: 'Date is required.', type: 'error' });
        return;
      }
      if (!punchTime) {
        validationResults.push({ row: rowNum, field: 'punchTime', message: 'Punch time is required.', type: 'error' });
        return;
      }

      const emp = MOCK_EMPLOYEES.find(e => e.employeeId === empId);
      if (!emp) {
        validationResults.push({ row: rowNum, field: 'employeeId', message: `Employee ID '${empId}' does not exist.`, type: 'error' });
        return;
      }
      if (emp.status === 'Inactive') {
        validationResults.push({ row: rowNum, field: 'employeeId', message: `Employee '${emp.full_name}' (${empId}) is inactive.`, type: 'error' });
        return;
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        validationResults.push({ row: rowNum, field: 'date', message: 'Invalid date format. Use YYYY-MM-DD.', type: 'error' });
        return;
      }
      if (!/^\d{2}:\d{2}$/.test(punchTime)) {
        validationResults.push({ row: rowNum, field: 'punchTime', message: 'Invalid time format. Use HH:mm (24-hour).', type: 'error' });
        return;
      }

      const key = `${empId}_${dateStr}_${punchTime}`;
      if (seenInFile.has(key)) {
        validationResults.push({ row: rowNum, field: 'punchTime', message: `Duplicate punch for Employee '${empId}' on '${dateStr}' at '${punchTime}'.`, type: 'warning' });
      } else {
        seenInFile.add(key);
      }

      validRows.push({ employeeId: empId, employeeName: emp.full_name, date: dateStr, punchTime });
    });

    const hasErrors = validationResults.some(r => r.type === 'error');
    return ok({
      valid: !hasErrors,
      totalRows: rows.length,
      errorCount: validationResults.filter(r => r.type === 'error').length,
      validRows,
      validationResults
    });
  }

  // POST /attendance/logs/raw-punches/upload  (group punches → first=in, last=out)
  if (req.method === 'POST' && url.includes('/attendance/logs/raw-punches/upload')) {
    const rows = req.body as any[];

    // Group punches by employeeId + date
    const grouped = new Map<string, { employeeId: string; employeeName: string; date: string; times: string[] }>();
    rows.forEach(row => {
      const empId     = row.employeeId;
      const dateStr   = row.date;
      const punchTime = row.punchTime;
      const key = `${empId}_${dateStr}`;
      if (!grouped.has(key)) {
        grouped.set(key, { employeeId: empId, employeeName: row.employeeName ?? '', date: dateStr, times: [] });
      }
      grouped.get(key)!.times.push(punchTime);
    });

    let inserted = 0;
    grouped.forEach(group => {
      group.times.sort();
      const checkIn  = group.times[0];
      const checkOut = group.times.length > 1 ? group.times[group.times.length - 1] : '';

      const existing = MOCK_ATTENDANCE_LOGS.findIndex(l => l.employeeId === group.employeeId && l.date === group.date);
      let workHours: number | null = null;
      if (checkIn && checkOut) {
        const [inH, inM]   = checkIn.split(':').map(Number);
        const [outH, outM] = checkOut.split(':').map(Number);
        workHours = Math.round(((outH * 60 + outM) - (inH * 60 + inM)) / 60 * 100) / 100;
      }
      const [inH, inM] = checkIn.split(':').map(Number);
      const status = !checkOut ? 'Missing Out-punch' : (inH * 60 + inM) > 9 * 60 ? 'Late' : 'Present';

      const newLog = {
        id: `al_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        employeeId: group.employeeId,
        employeeName: group.employeeName,
        date: group.date,
        checkIn,
        checkOut: checkOut || '',
        workHours,
        status,
        punchCount: group.times.length
      };

      if (existing >= 0) {
        MOCK_ATTENDANCE_LOGS[existing] = { ...MOCK_ATTENDANCE_LOGS[existing], ...newLog };
      } else {
        MOCK_ATTENDANCE_LOGS.push(newLog);
      }
      inserted++;
    });

    return ok({ message: `${inserted} attendance records processed from ${rows.length} raw punches.`, count: inserted });
  }

  // POST /attendance/logs/bulk-validate
  if (req.method === 'POST' && url.includes('/attendance/logs/bulk-validate')) {
    const rows = req.body as any[];
    const validationResults: any[] = [];
    const validRows: any[] = [];

    // Tracks seen in-file duplicates: employeeId + date combination
    const seenRecordsInFile = new Set<string>();

    rows.forEach((row, index) => {
      const rowNum = index + 2; // +1 for 0-index, +1 for headers
      
      const empId = (row.employeeId || '').trim();
      const dateStr = (row.date || '').trim();
      const checkInStr = (row.checkIn || '').trim();
      const checkOutStr = (row.checkOut || '').trim();

      let isRowValid = true;

      // 1. Missing required fields
      if (!empId) {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'employeeId', message: 'Employee ID is required.', type: 'error' });
      }
      if (!dateStr) {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'date', message: 'Date is required.', type: 'error' });
      }
      if (!checkInStr) {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'checkIn', message: 'Check-In time is required.', type: 'error' });
      }

      if (!isRowValid) {
        // If basic fields are missing, skip further validation for this row
        return;
      }

      // 2. Validate Employee existence and status
      const employee = MOCK_EMPLOYEES.find(e => e.employeeId === empId);
      if (!employee) {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'employeeId', message: `Employee ID '${empId}' does not exist.`, type: 'error' });
      } else if (employee.status === 'Inactive') {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'employeeId', message: `Employee '${employee.employeeName || employee.full_name}' (${empId}) is inactive.`, type: 'error' });
      }

      // 3. Date format validation (YYYY-MM-DD)
      const isDateFormatted = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
      if (!isDateFormatted) {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'date', message: 'Invalid date format. Use YYYY-MM-DD.', type: 'error' });
      } else {
        // Future date warning
        const rowDateObj = new Date(dateStr);
        const systemDateObj = new Date('2026-06-07');
        if (rowDateObj > systemDateObj) {
          validationResults.push({ row: rowNum, field: 'date', message: 'Attendance date is in the future.', type: 'warning' });
        }
      }

      // 4. Time format validation (HH:mm)
      const isCheckInFormatted = /^\d{2}:\d{2}$/.test(checkInStr);
      if (!isCheckInFormatted) {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'checkIn', message: 'Invalid time format. Use HH:mm (24-hour).', type: 'error' });
      }

      let isCheckOutFormatted = true;
      if (checkOutStr) {
        isCheckOutFormatted = /^\d{2}:\d{2}$/.test(checkOutStr);
        if (!isCheckOutFormatted) {
          isRowValid = false;
          validationResults.push({ row: rowNum, field: 'checkOut', message: 'Invalid time format. Use HH:mm (24-hour).', type: 'error' });
        }
      }

      // 5. Check-out earlier than check-in
      if (isCheckInFormatted && checkOutStr && isCheckOutFormatted) {
        const [inH, inM] = checkInStr.split(':').map(Number);
        const [outH, outM] = checkOutStr.split(':').map(Number);
        const inMins = inH * 60 + inM;
        const outMins = outH * 60 + outM;
        if (outMins < inMins) {
          isRowValid = false;
          validationResults.push({ row: rowNum, field: 'checkOut', message: 'Check-out time cannot be earlier than check-in time.', type: 'error' });
        }
      }

      // 6. In-file duplicate validation
      const recordKey = `${empId}_${dateStr}`;
      if (seenRecordsInFile.has(recordKey)) {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'employeeId', message: `Duplicate record for Employee ID '${empId}' on Date '${dateStr}' within the file.`, type: 'error' });
      } else {
        seenRecordsInFile.add(recordKey);
      }

      // 7. System duplicate validation
      const systemRecordExists = MOCK_ATTENDANCE_LOGS.some(log => log.employeeId === empId && log.date === dateStr);
      if (systemRecordExists) {
        isRowValid = false;
        validationResults.push({ row: rowNum, field: 'employeeId', message: `Attendance record already exists in the system for Employee ID '${empId}' on Date '${dateStr}'.`, type: 'error' });
      }

      if (isRowValid) {
        validRows.push({
          employeeId: empId,
          employeeName: employee ? (employee.employeeName || employee.full_name) : '',
          date: dateStr,
          checkIn: checkInStr,
          checkOut: checkOutStr || undefined
        });
      }
    });

    // Valid if no error types are in validationResults (warning types do not block import)
    const hasErrors = validationResults.some(r => r.type === 'error');

    return ok({
      valid: !hasErrors,
      totalRows: rows.length,
      errorCount: validationResults.filter(r => r.type === 'error').length,
      validRows: validRows,
      validationResults: validationResults
    });
  }

  // POST /attendance/logs/bulk-upload
  if (req.method === 'POST' && url.includes('/attendance/logs/bulk-upload')) {
    const rows = req.body as any[];
    
    rows.forEach(row => {
      // Calculate work hours if checkOut is present
      let workHours: number | undefined = undefined;
      if (row.checkIn && row.checkOut) {
        const [inH, inM] = row.checkIn.split(':').map(Number);
        const [outH, outM] = row.checkOut.split(':').map(Number);
        const diffMins = (outH * 60 + outM) - (inH * 60 + inM);
        workHours = Math.round((diffMins / 60) * 100) / 100;
      }

      // Compute dynamic status
      let status = 'Present';
      if (!row.checkOut) {
        status = 'Missing Out-punch';
      } else {
        const [inH, inM] = row.checkIn.split(':').map(Number);
        const checkInTimeMins = inH * 60 + inM;
        const lateThresholdMins = 9 * 60; // 09:00
        if (checkInTimeMins > lateThresholdMins) {
          status = 'Late';
        }
      }

      const newLog = {
        id: `al_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        employeeId: row.employeeId,
        employeeName: row.employeeName,
        date: row.date,
        checkIn: row.checkIn,
        checkOut: row.checkOut || '',
        workHours: workHours,
        status: status
      };

      MOCK_ATTENDANCE_LOGS.push(newLog);
    });

    return ok({
      message: `${rows.length} attendance records uploaded successfully.`,
      count: rows.length
    });
  }

  // ─── Attendance Correction Requests ──────────────────────────────────────

  // GET /attendance/my-attendance  (current user's own logs)
  if (req.method === 'GET' && url.includes('/attendance/my-attendance')) {
    const startDate = req.params.get('startDate');
    const endDate   = req.params.get('endDate');
    const status    = req.params.get('status');

    let result = MOCK_ATTENDANCE_LOGS.filter(l => l.employeeId === CURRENT_EMPLOYEE_ID);
    if (startDate) result = result.filter(l => l.date >= startDate);
    if (endDate)   result = result.filter(l => l.date <= endDate);
    if (status)    result = result.filter(l => l.status === status);

    const enriched = result.map(log => {
      let workHoursFormatted = log.workHoursFormatted;
      if (!workHoursFormatted && log.workHours != null) {
        const totalMin = Math.round(log.workHours * 60);
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        workHoursFormatted = `${h}h ${String(m).padStart(2, '0')}m`;
      }
      return {
        ...log,
        designation: 'Senior Software Engineer',
        department: 'Engineering',
        location: 'Colombo',
        shiftName: log.shiftName ?? 'IT Standard',
        dayType: log.dayType ?? 'Working Day',
        workHoursFormatted: workHoursFormatted ?? '—'
      };
    });
    enriched.sort((a, b) => b.date.localeCompare(a.date));
    return ok(enriched);
  }

  // PATCH /attendance/correction-requests/:id/cancel  (must be before /my and /:id)
  if (req.method === 'PATCH' && /\/attendance\/correction-requests\/[\w-]+\/cancel$/.test(url)) {
    const id  = url.split('/correction-requests/')[1].replace('/cancel', '');
    const idx = MOCK_CORRECTION_REQUESTS.findIndex(r => r.id === id);
    if (idx >= 0 && MOCK_CORRECTION_REQUESTS[idx].status === 'PENDING') {
      MOCK_CORRECTION_REQUESTS[idx] = { ...MOCK_CORRECTION_REQUESTS[idx], status: 'CANCELLED' };
    }
    return ok(null);
  }

  // PATCH /attendance/correction-requests/:id/approve
  if (req.method === 'PATCH' && /\/attendance\/correction-requests\/[\w-]+\/approve$/.test(url)) {
    const id  = url.split('/correction-requests/')[1].replace('/approve', '');
    const idx = MOCK_CORRECTION_REQUESTS.findIndex(r => r.id === id);
    if (idx >= 0) {
      MOCK_CORRECTION_REQUESTS[idx] = {
        ...MOCK_CORRECTION_REQUESTS[idx],
        status: 'APPROVED',
        reviewedAt: new Date().toISOString(),
        reviewedBy: 'Admin User',
        reviewerComments: (req.body as any)?.reviewerComments ?? ''
      };
      return ok(MOCK_CORRECTION_REQUESTS[idx]);
    }
    return ok(null);
  }

  // PATCH /attendance/correction-requests/:id/reject
  if (req.method === 'PATCH' && /\/attendance\/correction-requests\/[\w-]+\/reject$/.test(url)) {
    const id  = url.split('/correction-requests/')[1].replace('/reject', '');
    const idx = MOCK_CORRECTION_REQUESTS.findIndex(r => r.id === id);
    if (idx >= 0) {
      MOCK_CORRECTION_REQUESTS[idx] = {
        ...MOCK_CORRECTION_REQUESTS[idx],
        status: 'REJECTED',
        reviewedAt: new Date().toISOString(),
        reviewedBy: 'Admin User',
        reviewerComments: (req.body as any)?.reviewerComments ?? ''
      };
      return ok(MOCK_CORRECTION_REQUESTS[idx]);
    }
    return ok(null);
  }

  // GET /attendance/correction-requests/my  (must be before /:id)
  if (req.method === 'GET' && url.includes('/attendance/correction-requests/my')) {
    const mine = MOCK_CORRECTION_REQUESTS
      .filter(r => r.employeeId === CURRENT_EMPLOYEE_ID)
      .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
    return ok(mine);
  }

  // GET /attendance/correction-requests  (admin — all requests)
  if (req.method === 'GET' && url.includes('/attendance/correction-requests')) {
    const status    = req.params.get('status');
    const startDate = req.params.get('startDate');
    const endDate   = req.params.get('endDate');

    let result = [...MOCK_CORRECTION_REQUESTS];
    if (status)    result = result.filter(r => r.status === status);
    if (startDate) result = result.filter(r => r.attendanceDate >= startDate);
    if (endDate)   result = result.filter(r => r.attendanceDate <= endDate);
    result.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
    return ok(result);
  }

  // POST /attendance/correction-requests  (submit new request)
  if (req.method === 'POST' && url.includes('/attendance/correction-requests')) {
    const body = req.body as any;
    const currentEmp = MOCK_ATTENDANCE_LOGS.find(l => l.employeeId === CURRENT_EMPLOYEE_ID);
    const newReq: AttendanceCorrectionRequest = {
      id: nextCorrectionId(),
      employeeId: CURRENT_EMPLOYEE_ID,
      employeeName: currentEmp?.employeeName ?? 'Aisha Khan',
      department: 'Engineering',
      designation: 'Senior Software Engineer',
      attendanceDate: body.attendanceDate,
      shiftName: body.shiftName,
      correctionType: body.correctionType,
      originalInTime: body.originalInTime,
      originalOutTime: body.originalOutTime,
      requestedInTime: body.requestedInTime,
      requestedOutTime: body.requestedOutTime,
      reason: body.reason,
      status: 'PENDING',
      submittedAt: new Date().toISOString()
    };
    MOCK_CORRECTION_REQUESTS.unshift(newReq);
    return created(newReq);
  }

  // ─── Shift Assignments ────────────────────────────────────────────────────

  // DELETE /shift-assignments/:id/members/:employeeId  (must be checked before /:id)
  if (req.method === 'DELETE' && /\/shift-assignments\/[\w-]+\/members\/[\w-]+$/.test(url)) {
    const afterBase  = url.split('/shift-assignments/')[1];
    const [assignId, employeeId] = afterBase.split('/members/');
    const idx = MOCK_SHIFT_ASSIGNMENTS.findIndex(a => a.id === assignId);
    if (idx >= 0) {
      MOCK_SHIFT_ASSIGNMENTS[idx].members = (MOCK_SHIFT_ASSIGNMENTS[idx].members ?? []).filter(m => m.employeeId !== employeeId);
      MOCK_SHIFT_ASSIGNMENTS[idx].memberCount = MOCK_SHIFT_ASSIGNMENTS[idx].members!.length;
    }
    return ok(MOCK_SHIFT_ASSIGNMENTS[idx] ?? null);
  }

  // GET /shift-assignments
  if (req.method === 'GET' && /\/shift-assignments$/.test(url)) {
    const shiftId = req.params.get('shiftId');
    let result = MOCK_SHIFT_ASSIGNMENTS as ShiftAssignment[];
    if (shiftId) result = result.filter(a => a.shiftId === shiftId);
    return ok([...result]);
  }

  // GET /shift-assignments/:id
  if (req.method === 'GET' && /\/shift-assignments\/[\w-]+$/.test(url)) {
    const id = url.split('/shift-assignments/')[1];
    return ok(MOCK_SHIFT_ASSIGNMENTS.find(a => a.id === id) ?? null);
  }

  // POST /shift-assignments
  if (req.method === 'POST' && /\/shift-assignments$/.test(url)) {
    const body = req.body as any;
    let newAssignment: ShiftAssignment;

    if (body.type === 'ROTATION') {
      // Derive flat members list from all rotation groups
      const allMemberIds = new Set<string>();
      (body.rotationGroups as RotationGroup[] ?? []).forEach(rg => {
        if (rg.groupId) {
          const sg = MOCK_GROUPS.find(g => g.id === rg.groupId);
          (sg?.members ?? []).forEach(m => allMemberIds.add(m.employeeId));
        }
        (rg.memberIds ?? []).forEach((id: string) => allMemberIds.add(id));
      });
      const members = Array.from(allMemberIds).map(empId => {
        const e = MOCK_EMPLOYEES.find(x => x.employeeId === empId);
        return { employeeId: empId, employeeName: e?.employeeName ?? e?.full_name ?? '', designation: e?.designation ?? '', department: e?.department ?? '' };
      });
      newAssignment = {
        id: nextAssignmentId(), type: 'ROTATION', name: body.name,
        startDate: body.startDate, endDate: body.endDate,
        intervalValue: body.intervalValue, intervalUnit: body.intervalUnit,
        cycles: body.cycles, rotationGroups: body.rotationGroups,
        memberCount: members.length, members, createdAt: new Date().toISOString()
      };
    } else {
      const shift   = MOCK_SHIFTS.find(s => s.id === body.shiftId);
      const members = (body.employeeIds as string[] ?? []).map(empId => {
        const e = MOCK_EMPLOYEES.find(x => x.employeeId === empId);
        return { employeeId: empId, employeeName: e?.employeeName ?? e?.full_name ?? '', designation: e?.designation ?? '', department: e?.department ?? '' };
      });
      newAssignment = {
        id: nextAssignmentId(), type: 'NORMAL', name: body.name,
        shiftId: body.shiftId, shiftName: shift?.name ?? '',
        startDate: body.startDate, endDate: body.endDate,
        memberCount: members.length, members, createdAt: new Date().toISOString()
      };
    }

    MOCK_SHIFT_ASSIGNMENTS.unshift(newAssignment);
    return created(newAssignment);
  }

  // PUT /shift-assignments/:id
  if (req.method === 'PUT' && /\/shift-assignments\/[\w-]+$/.test(url)) {
    const id  = url.split('/shift-assignments/')[1];
    const idx = MOCK_SHIFT_ASSIGNMENTS.findIndex(a => a.id === id);
    if (idx >= 0) {
      const patch = req.body as any;
      const cur   = { ...MOCK_SHIFT_ASSIGNMENTS[idx] };

      if (patch.name)      cur.name      = patch.name;
      if (patch.startDate) cur.startDate = patch.startDate;
      if (patch.endDate)   cur.endDate   = patch.endDate;

      if (cur.type === 'NORMAL' || patch.type === 'NORMAL') {
        if (patch.shiftId) {
          cur.shiftId   = patch.shiftId;
          cur.shiftName = MOCK_SHIFTS.find(s => s.id === patch.shiftId)?.name ?? cur.shiftName;
        }
        if (patch.employeeIds) {
          cur.members = (patch.employeeIds as string[]).map(empId => {
            const e = MOCK_EMPLOYEES.find(x => x.employeeId === empId);
            return { employeeId: empId, employeeName: e?.employeeName ?? e?.full_name ?? '', designation: e?.designation ?? '', department: e?.department ?? '' };
          });
          cur.memberCount = cur.members.length;
        }
      } else {
        // ROTATION
        if (patch.intervalValue != null) cur.intervalValue = patch.intervalValue;
        if (patch.intervalUnit)          cur.intervalUnit  = patch.intervalUnit;
        if (patch.cycles)                cur.cycles        = patch.cycles;
        if (patch.rotationGroups) {
          cur.rotationGroups = patch.rotationGroups;
          const allMemberIds = new Set<string>();
          (patch.rotationGroups as RotationGroup[]).forEach(rg => {
            if (rg.groupId) {
              const sg = MOCK_GROUPS.find(g => g.id === rg.groupId);
              (sg?.members ?? []).forEach(m => allMemberIds.add(m.employeeId));
            }
            (rg.memberIds ?? []).forEach((id: string) => allMemberIds.add(id));
          });
          cur.members = Array.from(allMemberIds).map(empId => {
            const e = MOCK_EMPLOYEES.find(x => x.employeeId === empId);
            return { employeeId: empId, employeeName: e?.employeeName ?? e?.full_name ?? '', designation: e?.designation ?? '', department: e?.department ?? '' };
          });
          cur.memberCount = cur.members.length;
        }
      }

      MOCK_SHIFT_ASSIGNMENTS[idx] = cur;
      return ok(cur);
    }
    return ok(null);
  }

  // DELETE /shift-assignments/:id
  if (req.method === 'DELETE' && /\/shift-assignments\/[\w-]+$/.test(url)) {
    const id  = url.split('/shift-assignments/')[1];
    const idx = MOCK_SHIFT_ASSIGNMENTS.findIndex(a => a.id === id);
    if (idx >= 0) MOCK_SHIFT_ASSIGNMENTS.splice(idx, 1);
    return noContent();
  }

  // ─── Shifts ───────────────────────────────────────────────────────────────

  // GET /shifts
  if (req.method === 'GET' && /\/shifts$/.test(url)) {
    return ok(MOCK_SHIFTS);
  }

  // GET /shifts/:id
  if (req.method === 'GET' && /\/shifts\/[\w-]+$/.test(url)) {
    const id = url.split('/shifts/')[1];
    const shift = MOCK_SHIFTS.find(s => s.id === id);
    return ok(shift ?? null);
  }

  // POST /shifts
  if (req.method === 'POST' && /\/shifts$/.test(url)) {
    const body = req.body as Shift;
    const created_shift: Shift = { ...body, id: nextShiftId(), createdAt: new Date().toISOString() };
    MOCK_SHIFTS.push(created_shift);
    return created(created_shift);
  }

  // PUT /shifts/:id
  if (req.method === 'PUT' && /\/shifts\/[\w-]+$/.test(url)) {
    const id = url.split('/shifts/')[1];
    const idx = MOCK_SHIFTS.findIndex(s => s.id === id);
    if (idx >= 0) {
      MOCK_SHIFTS[idx] = { ...MOCK_SHIFTS[idx], ...(req.body as object), id, updatedAt: new Date().toISOString() };
      return ok(MOCK_SHIFTS[idx]);
    }
    return ok(null);
  }

  // DELETE /shifts/:id
  if (req.method === 'DELETE' && /\/shifts\/[\w-]+$/.test(url)) {
    const id = url.split('/shifts/')[1];
    const idx = MOCK_SHIFTS.findIndex(s => s.id === id);
    if (idx >= 0) MOCK_SHIFTS.splice(idx, 1);
    return noContent();
  }


  // GET /shift-groups
  if (req.method === 'GET' && /\/shift-groups$/.test(url)) {
    return ok(MOCK_GROUPS.map(g => ({ ...g, members: undefined })));
  }

  // GET /shift-groups/:id
  if (req.method === 'GET' && /\/shift-groups\/[\w-]+$/.test(url)) {
    const id = url.split('/shift-groups/')[1];
    const group = MOCK_GROUPS.find(g => g.id === id);
    return ok(group ?? null);
  }

  // POST /shift-groups
  if (req.method === 'POST' && /\/shift-groups$/.test(url)) {
    const body = req.body as ShiftGroup;
    const newGroup: ShiftGroup = {
      ...body,
      id: nextGroupId(),
      memberCount: body.members?.length ?? 0,
      membershipType: 'STATIC',
      createdAt: new Date().toISOString()
    };
    MOCK_GROUPS.push(newGroup);
    return created(newGroup);
  }

  // PUT /shift-groups/:id
  if (req.method === 'PUT' && /\/shift-groups\/[\w-]+$/.test(url)) {
    const id = url.split('/shift-groups/')[1];
    const idx = MOCK_GROUPS.findIndex(g => g.id === id);
    if (idx >= 0) {
      const updated = { ...MOCK_GROUPS[idx], ...(req.body as object), id, updatedAt: new Date().toISOString() };
      updated.memberCount = updated.members?.length ?? 0;
      MOCK_GROUPS[idx] = updated;
      return ok(MOCK_GROUPS[idx]);
    }
    return ok(null);
  }

  // DELETE /shift-groups/:id
  if (req.method === 'DELETE' && /\/shift-groups\/[\w-]+$/.test(url)) {
    const id = url.split('/shift-groups/')[1];
    const idx = MOCK_GROUPS.findIndex(g => g.id === id);
    if (idx >= 0) MOCK_GROUPS.splice(idx, 1);
    return noContent();
  }

  // POST /shift-groups/:id/members
  if (req.method === 'POST' && /\/shift-groups\/[\w-]+\/members$/.test(url)) {
    const id = url.split('/shift-groups/')[1].split('/members')[0];
    const group = MOCK_GROUPS.find(g => g.id === id);
    const { employeeIds } = req.body as { employeeIds: string[] };
    if (group) {
      const existing = new Set((group.members ?? []).map(m => m.employeeId));
      const toAdd = MOCK_EMPLOYEES.filter(e => employeeIds.includes(e.employeeId) && !existing.has(e.employeeId));
      group.members = [...(group.members ?? []), ...toAdd];
      group.memberCount = group.members.length;
    }
    return ok(null);
  }

  // DELETE /shift-groups/:id/members
  if (req.method === 'DELETE' && /\/shift-groups\/[\w-]+\/members$/.test(url)) {
    const id = url.split('/shift-groups/')[1].split('/members')[0];
    const group = MOCK_GROUPS.find(g => g.id === id);
    const { employeeIds } = req.body as { employeeIds: string[] };
    if (group) {
      group.members = (group.members ?? []).filter(m => !employeeIds.includes(m.employeeId));
      group.memberCount = group.members.length;
    }
    return noContent();
  }

  // Holiday Calendars routes are handled by the real backend pass through.

  return next(req);
};

